import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Download } from "lucide-react";
import HeroMesh from "./HeroMesh";
import { CONTENT } from "../data/content";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const line = {
  hidden: { y: "115%" },
  show: {
    y: 0,
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] },
  },
};

const fade = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const scrollDown = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 md:pt-32 pb-24"
    >
      <div className="absolute inset-0 blueprint-grid opacity-40" />
      <HeroMesh />

      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 min-h-[calc(100svh-13rem)] flex items-center"
      >
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* LEFT — text */}
          <motion.div style={{ y }} className="order-2 lg:order-1 lg:col-span-7">
            {/* Top meta row */}
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="flex flex-wrap items-center gap-3 md:gap-6 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground mb-8 md:mb-10"
            >
              <span className="mask-line">
                <motion.span variants={line} className="inline-block">
                  <span className="text-accent">◆</span> Portfolio / 2026
                </motion.span>
              </span>
            </motion.div>

            {/* Kinetic headline */}
            <motion.h1
              variants={container}
              initial="hidden"
              animate="show"
              className="font-heading font-black tracking-[-0.04em] text-foreground leading-[0.86] text-[15vw] sm:text-[13vw] lg:text-[6.5rem] xl:text-[7.5rem] uppercase"
              data-testid="hero-headline"
            >
              <span className="mask-line block">
                <motion.span variants={line} className="inline-block">
                  Cloud
                </motion.span>
              </span>
              <span className="mask-line block">
                <motion.span variants={line} className="inline-block">
                  Security
                </motion.span>
              </span>
              <span className="mask-line block">
                <motion.span variants={line} className="inline-block text-accent italic font-light tracking-tight">
                  Engineer<span className="text-accent not-italic font-black">.</span>
                </motion.span>
              </span>
            </motion.h1>

            {/* Value prop */}
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="mt-8 md:mt-10 space-y-4 max-w-[46ch]"
            >
              <span className="mask-line">
                <motion.span
                  variants={line}
                  className="inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground"
                >
                  — 7.5 years / 4 clouds / security engineering that ships
                </motion.span>
              </span>
              <motion.p variants={fade} className="text-lg md:text-xl leading-snug text-foreground/90">
                I secure and operate multi-cloud environments across{" "}
                <span className="text-accent font-medium">AWS</span>,{" "}
                <span className="text-accent font-medium">Azure</span> and{" "}
                <span className="text-accent font-medium">GCP</span> — with a specialism in
                enterprise IAM &amp; identity governance.
              </motion.p>
            </motion.div>

            {/* CTAs */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              className="mt-8 md:mt-10 flex flex-wrap gap-3"
            >
              <a
                data-testid="hero-download-resume"
                href={CONTENT.contact.resumeUrl}
                download
                className="group inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors"
              >
                <Download size={14} />
                Download résumé
              </a>
              <a
                data-testid="hero-contact-cta"
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-2 border border-border px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors"
              >
                Contact me
                <span className="w-1.5 h-1.5 bg-accent group-hover:scale-125 transition-transform" />
              </a>
            </motion.div>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              data-testid="hero-open-to"
              className="mt-5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-foreground/80"
            >
              <span className="text-accent">◆</span> Open to senior cloud security / platform security roles — remote or UAE.
            </motion.p>

            {/* Running-system callout */}
            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              data-testid="hero-intel-callout"
              className="mt-3 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-muted-foreground leading-relaxed max-w-[72ch]"
            >
              <span className="text-accent">Currently running</span> — Tech Intelligence: automated ingestion, scoring and
              summarisation across nine cloud and DevOps sources, every six hours.{" "}
              <a
                href="#intelligence"
                data-testid="hero-intel-link"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("intelligence")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-accent hover:text-foreground transition-colors whitespace-nowrap"
              >
                [ View → ]
              </a>
            </motion.p>
          </motion.div>

          {/* RIGHT — photo */}
          <motion.div
            style={{ y: yPhoto }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 lg:order-2 lg:col-span-5 flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Accent corner ticks for editorial feel */}
              <div className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-accent" />
              <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-accent" />
              <div
                data-testid="hero-photo-frame"
                className="relative w-[220px] h-[220px] sm:w-[260px] sm:h-[260px] lg:w-[320px] lg:h-[320px] xl:w-[360px] xl:h-[360px] rounded-2xl overflow-hidden ring-1 ring-accent/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] bg-surface"
              >
                <img
                  data-testid="hero-photo"
                  src={CONTENT.hero.photo}
                  alt={CONTENT.hero.photoAlt}
                  loading="lazy"
                  width={720}
                  height={720}
                  className="w-full h-full object-cover object-[center_20%]"
                />
              </div>
              {/* Caption chip */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 lg:left-auto lg:right-4 lg:translate-x-0 bg-background border border-border px-3 py-1.5 whitespace-nowrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  AWS SA — <span className="text-accent">Professional</span>
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom rail */}
      <div className="absolute bottom-6 md:bottom-8 left-0 right-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-end justify-between font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          <button
            onClick={scrollDown}
            data-testid="hero-scroll-cue"
            className="group inline-flex items-center gap-2 hover:text-accent transition-colors"
          >
            <span className="w-8 h-px bg-current group-hover:bg-accent transition-colors" />
            Scroll <ArrowDown size={12} className="animate-bounce" />
          </button>
          <div className="hidden md:block">Cloud Security Engineer / +91 837 446 2572</div>
        </div>
      </div>
    </section>
  );
}
